import React, { useCallback, useEffect, useMemo, useState } from "react";

import Modal from "../Modal";
import TagHeatMap from "../tags/TagHeatMap";
import { useAuth } from "../../contexts/AuthContext";
import { cn } from "../../lib/utils";
import {
  fetchUnhealthyTags,
  type UnhealthyTag,
  type UnhealthyTagsResponse,
} from "../../services/alertsApi";

/** Window options for the ?days filter. null = all-time. */
const WINDOWS: { label: string; days: number | null }[] = [
  { label: "All time", days: null },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
];

function BucketBadge({ bucket }: { bucket: UnhealthyTag["bucket"] }): React.ReactElement {
  const isWorst = bucket === "UNRELIABLE";
  return (
    <span
      className={cn(
        "inline-block px-2 py-0.5 rounded text-xs font-semibold",
        isWorst
          ? "bg-red-100 text-red-700"
          : "bg-amber-100 text-amber-700",
      )}
    >
      {bucket}
    </span>
  );
}

function pct(ratio: number | null): string {
  return ratio == null ? "—" : `${Math.round(ratio * 100)}%`;
}

/** Group tags by box, worst-bucket-heavy boxes first (the API already returns
 *  UNRELIABLE-first, box, EPC — so preserving order keeps that within a box). */
interface BoxGroup {
  key: string;
  label: string;
  tags: UnhealthyTag[];
}
function groupByBox(tags: UnhealthyTag[]): BoxGroup[] {
  const map = new Map<string, BoxGroup>();
  for (const t of tags) {
    const key = t.scu_name;
    const label = t.smartbox_code || t.scu_name;
    let g = map.get(key);
    if (!g) {
      g = { key, label, tags: [] };
      map.set(key, g);
    }
    g.tags.push(t);
  }
  return Array.from(map.values());
}

export const UnhealthyTagsTab: React.FC = () => {
  const { token } = useAuth();
  // Click an EPC to see which antennas are reading it. This is the tab where
  // you are already asking "why is this tag flaky?", and "only antenna 3 sees
  // it, at the far end of the box" is usually the answer -- so the heat map
  // belongs one click away, not on another page.
  const [heatMapFor, setHeatMapFor] = useState<string | null>(null);
  const [days, setDays] = useState<number | null>(null);
  const [data, setData] = useState<UnhealthyTagsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    fetchUnhealthyTags(token, days)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token, days]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(
    () => (data ? groupByBox(data.tags) : []),
    [data],
  );

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex rounded-md border border-secondary-300 overflow-hidden">
          {WINDOWS.map((w) => (
            <button
              key={w.label}
              type="button"
              onClick={() => setDays(w.days)}
              className={cn(
                "px-3 py-1.5 text-sm",
                days === w.days
                  ? "bg-primary-500 text-white"
                  : "bg-white text-secondary-700 hover:bg-secondary-50",
              )}
            >
              {w.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
        {data && (
          <span className="text-sm text-secondary-600">
            {data.count} unhealthy tag{data.count === 1 ? "" : "s"} across{" "}
            {groups.length} box{groups.length === 1 ? "" : "es"}
          </span>
        )}
      </div>

      <p className="text-xs text-secondary-500 mb-4">
        FLICKERY / UNRELIABLE tags currently on-hand across your visible
        smartboxes. UNRELIABLE is the least stable; a tag reads intermittently
        (FLICKERY) or barely at all (UNRELIABLE). Matches the Dashboard
        tag-health tile's abnormal count.
      </p>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn’t load unhealthy tags: {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-sm text-secondary-500">Loading…</div>
      )}

      {data && data.count === 0 && !loading && (
        <div className="rounded-md border border-secondary-200 bg-secondary-50 px-4 py-6 text-center text-sm text-secondary-600">
          No unhealthy tags in this window. 🎉
        </div>
      )}

      {/* Antenna heat map for one tag. The server answers "not on hand" for a
          tag that has left the box, so an unhealthy tag that has since departed
          says so rather than drawing a stale position. */}
      <Modal
        open={!!heatMapFor}
        onClose={() => setHeatMapFor(null)}
        title={heatMapFor ? `Antennas: ${heatMapFor}` : "Antennas"}
      >
        {heatMapFor && <TagHeatMap epc={heatMapFor} />}
      </Modal>

      {groups.map((g) => (
        <div key={g.key} className="mb-6">
          <div className="flex items-baseline gap-2 mb-2">
            <h3 className="text-sm font-semibold text-secondary-900">{g.label}</h3>
            <span className="text-xs text-secondary-500">
              {g.tags.length} tag{g.tags.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-secondary-500 border-b border-secondary-200">
                  <th className="py-1.5 pr-3 font-medium">Bucket</th>
                  <th className="py-1.5 pr-3 font-medium">SKU</th>
                  <th className="py-1.5 pr-3 font-medium">Item</th>
                  <th className="py-1.5 pr-3 font-medium">EPC</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Reads</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Longest miss</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Current miss</th>
                </tr>
              </thead>
              <tbody>
                {g.tags.map((t) => (
                  <tr
                    key={t.epc}
                    className="border-b border-secondary-100 last:border-0"
                  >
                    <td className="py-1.5 pr-3">
                      <BucketBadge bucket={t.bucket} />
                    </td>
                    <td className="py-1.5 pr-3">{t.sku ?? "—"}</td>
                    <td className="py-1.5 pr-3 max-w-xs truncate" title={t.item_name ?? ""}>
                      {t.item_name ?? <span className="text-secondary-400">unmatched</span>}
                    </td>
                    <td className="py-1.5 pr-3 font-mono text-xs">
                      <button
                        type="button"
                        onClick={() => setHeatMapFor(t.epc)}
                        title={`${t.epc} — show which antennas are reading this tag`}
                        className="text-primary-600 hover:underline font-mono"
                      >
                        {t.epc}
                      </button>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {t.read_scans}/{t.total_scans}{" "}
                      <span className="text-secondary-400">({pct(t.read_ratio)})</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{t.longest_miss}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {t.current_miss_streak}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UnhealthyTagsTab;
